import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArModelTooLargeError, WebArService } from '@/ui-kit/domain/WebArService';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { API } from '@canvas-logic/clp-viewer3d';
import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';
import { NotificationType } from '@/ui-kit/services/NotificationService';

type State = 'idle' | 'ready' | 'loading' | 'error';

export type ArLinkError = { kind: 'too-large'; size: number; limit: number } | { kind: 'failed' };

type Args = {
  api: API;
};

type Value = {
  state: State;
  link: string;
  error?: ArLinkError;
  generate: () => Promise<void>;
  reset: () => void;
};

/** Labelled MB but measured in MiB, matching the limits the service is configured with. */
const toMegabytes = (bytes: number): string => (bytes / 1024 / 1024).toFixed(1);

/**
 * @todo get API from `useBootstrapper`
 */
export const useGetArLink = ({ api }: Args) => {
  const [state, setState] = useState<State>('idle');
  const [link, setLink] = useState('');
  const [error, setError] = useState<ArLinkError>();
  const { bootstrapper } = useBootstrapper();
  const { t } = useTranslation();

  const webArService = useMemo(() => {
    return new WebArService(api);
  }, [api]);

  const generate = useCallback(async () => {
    if (state === 'loading') {
      return;
    }

    if (state !== 'ready') {
      setState('loading');
      setError(undefined);
      try {
        const link = await webArService.generateArLink(bootstrapper!.projectId.toString());
        const hostDeviceType = await embeddingService.getHostDeviceType();
        if (hostDeviceType === 'desktop') {
          setState('ready');
          setLink(link);
        } else {
          window.location.replace(link);
        }
      } catch (e: unknown) {
        console.error(e);
        const failure: ArLinkError =
          e instanceof ArModelTooLargeError ? { kind: 'too-large', size: e.size, limit: e.limit } : { kind: 'failed' };
        setError(failure);
        setState('error');
        // Both entry points (button and menu item) use this hook, so the message is raised here rather
        // than in each of them.
        void bootstrapper?.notificationService.showNotification({
          type: NotificationType.ERROR,
          modal: true,
          content:
            failure.kind === 'too-large'
              ? t('errors.arModelTooLarge', {
                  size: toMegabytes(failure.size),
                  limit: toMegabytes(failure.limit),
                })
              : t('errors.arLinkFailed'),
        });
      }
    }
  }, [state, webArService, bootstrapper, t]);

  const reset = useCallback(() => {
    setState('idle');
    setError(undefined);
  }, []);

  return useMemo<Value>(
    () => ({
      state,
      link,
      error,
      generate,
      reset,
    }),
    [state, link, error, generate, reset],
  );
};
